import React, { useMemo } from 'react';
import { Nav, NavItem, NavLink } from 'reactstrap';
import { ReplaceLink } from 'js/components';
import { useParams, matchPath } from 'react-router-dom';
import { CONNECTOR_SERVICES } from 'js/data/connectors';
import { getConnectorDashboardEntry } from 'js/connectors';

const TABS = {
  ACTIVITY: 'activity',
  RUBRICS: 'rubrics',
  SETTINGS: 'settings',
};

function getActiveTab(pathname, basePath) {
  const connectorMatch = matchPath(pathname, {
    path: `${basePath}/connectors/:connectorComponent/:connectorId`,
  });
  if (connectorMatch) {
    return {
      type: 'connector',
      connectorId: connectorMatch.params.connectorId,
    };
  }
  if (null != matchPath(pathname, { path: `${basePath}/settings` })) {
    return TABS.SETTINGS;
  }
  if (null != matchPath(pathname, { path: `${basePath}/rubrics` })) {
    return TABS.RUBRICS;
  }
  return TABS.ACTIVITY;
}

/**
 * Shared tab navigation for team dashboard, settings, and connector views.
 * Accepts teamConnectorConfigurations to build connector tabs and URLs.
 */
function TeamTabs({
  teamConnectorConfigurations = [],
  teamConnectorConfigurationsHasLoaded = false,
}) {
  const { workspaceId, teamId } = useParams();
  const basePath = `/w/${workspaceId}/teams/${teamId}`;
  const pathname =
    typeof window !== 'undefined' ? window.location.pathname : '';

  const matchedTab = useMemo(
    () => getActiveTab(pathname, basePath),
    [pathname, basePath]
  );

  const connectorTabs = useMemo(() => {
    if (
      !teamConnectorConfigurationsHasLoaded ||
      !teamConnectorConfigurations?.length
    )
      return [];
    return teamConnectorConfigurations
      .map((config) => {
        const entry = getConnectorDashboardEntry(config.connector?.type);
        if (!entry || !config.connector) return null;
        const service = CONNECTOR_SERVICES.find(
          (s) => s.id === config.connector.type
        );
        const serviceName = service?.name ?? config.connector.type;
        const connectorName = config.connector.name ?? '';
        const label = connectorName
          ? `${serviceName} – ${connectorName}`
          : serviceName;
        return {
          key: String(config.connector.id),
          label,
          to: `/w/${workspaceId}/teams/${teamId}/connectors/${entry.routeSlug}/${config.connector.id}`,
          routeSlug: entry.routeSlug,
        };
      })
      .filter(Boolean);
  }, [
    teamConnectorConfigurations,
    teamConnectorConfigurationsHasLoaded,
    workspaceId,
    teamId,
  ]);

  const isConnectorTabActive = (tabConnectorId) =>
    matchedTab?.type === 'connector' &&
    String(matchedTab.connectorId) === String(tabConnectorId);

  return (
    <Nav className="tab-nav nav-underline">
      <NavItem>
        <NavLink
          tag={ReplaceLink}
          active={matchedTab === TABS.ACTIVITY}
          data-view="active"
          to={`/w/${workspaceId}/teams/${teamId}/dashboard`}
        >
          Activity
        </NavLink>
      </NavItem>
      <NavItem>
        <NavLink
          tag={ReplaceLink}
          active={matchedTab === TABS.RUBRICS}
          data-view="rubrics"
          to={`/w/${workspaceId}/teams/${teamId}/rubrics`}
        >
          Rubrics
        </NavLink>
      </NavItem>
      {connectorTabs.map((tab) => (
        <NavItem key={tab.key}>
          <NavLink
            tag={ReplaceLink}
            active={isConnectorTabActive(tab.key)}
            data-view={`connector-${tab.routeSlug}`}
            to={tab.to}
          >
            {tab.label}
          </NavLink>
        </NavItem>
      ))}
      <NavItem>
        <NavLink
          tag={ReplaceLink}
          data-view="settings"
          to={`/w/${workspaceId}/teams/${teamId}/settings`}
          active={matchedTab === TABS.SETTINGS}
        >
          Settings
        </NavLink>
      </NavItem>
    </Nav>
  );
}

export default TeamTabs;
